import { AddRounded, SearchRounded, UploadFileRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  InputAdornment,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import useUser from "../../../providers/user/user_provider";
import DataGridJobListing from "../components/data_grid_job_listing/data_grid_job_listing";
import { JobListingModel } from "../model/job_listing_model";
import FilterJobListing from "../components/filer_job_listing/filter_job_listing";
import {
  getJobList,
  downloadJobListingService,
  updateJobStatus,
  deleteJob,
} from "../../../services/jobs/jobs";
import { downloadFromBlob } from "../../../utils/utils";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import Loading from "../../../components/loading/loading";
import UpdateJobsStatusToReject from "../components/update_jobs_status/update_jobs_status_popup";
import UpdateJobsStatusToDelete from "../components/update_jobs_status/update_jobs_status_popup";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../../constants";
import { useJobsFilterStore } from "../../../store/use-jobs-filter-store";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import OrgSelector from "../../../components/org_selector/org_selector";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import { TableEmptyState, TableFooterPagination } from "../../users/components/table_toolkit/table_toolkit";
import FilterIcon from "../../../assets/filter_icon.svg";
import SortIcon from "../../../assets/sort.svg";
import XLSIcon from "../../../assets/xls_icon.svg";
import SortJobsPopup, {
  DEFAULT_JOBS_SORT_OPTION,
  JobsSortOption,
  jobsSortOptionLabel,
  jobsSortOptionToParams,
} from "../components/sort_jobs_popup/sort_jobs_popup";
import BulkUploadJobsDialog from "../components/bulk_upload_jobs/bulk_upload_jobs_dialog";
import { black, greyEnabled, purple } from "../../../theme/colors";

const SEARCH_DEBOUNCE_MS = 500;
const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function JobListing({
  page,
  setPage,
  currentTab,
}: {
  page: number;
  setPage: (val: number) => void;
  currentTab: number;
}) {
  const navigate = useNavigate();
  const { hasPermission } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState<string>("");
  const [pageSize, setPageSize] = useState(8);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<JobsSortOption>(DEFAULT_JOBS_SORT_OPTION);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<JobListingModel | null>(null);
  const [exportSubmitting, setExportSubmitting] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  // Which row's action (approve/reject/delete) currently has an API call in
  // flight - shown as a spinner over that row's action button, since the
  // Reject/Delete dialogs close immediately on submit, before their API
  // call actually resolves, leaving no other feedback that it's still
  // processing.
  const [actionLoading, setActionLoading] = useState<{ jobId: number; action: "approve" | "reject" | "delete" } | null>(null);
  const { showSnackbar } = useFeedback();
  const {
    stateId,
    cityId,
    districtId,
    jobType,
    experience,
    selectedStatus,
    categoryId,
    subCategoryId,
    startDate,
    endDate,
    ageFrom,
    ageTo,
    gender,
    source,
    searchValue,
    setSearchValue,
  } = useJobsFilterStore();
  const { selectedOrgId } = useSelectedOrgStore();

  const activeFilterCount = [
    stateId,
    cityId,
    districtId,
    jobType,
    experience,
    selectedStatus,
    source,
    categoryId,
    subCategoryId,
    startDate && endDate,
    gender,
  ].filter(Boolean).length;

  useEffect(() => {
    setSearchInput(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (searchInput === searchValue) return;
    const timeoutId = setTimeout(() => {
      setPage(1);
      setSearchValue(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchInput, searchValue, setPage, setSearchValue]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, sortOption, selectedOrgId]);

  useEffect(() => {
    const currentPage = searchParams.get("page");
    const newPage = page.toString();
    if (currentPage !== newPage) {
      setSearchParams({ page: newPage, currentTab: currentTab?.toString() }, { replace: true });
    }
  }, [page, setSearchParams, currentTab, searchParams]);

  const { data: jobList, isLoading, isError } = useQuery(
    [
      "JobList",
      page,
      pageSize,
      cityId,
      districtId,
      experience,
      searchValue,
      stateId,
      selectedStatus,
      jobType,
      categoryId,
      subCategoryId,
      startDate,
      endDate,
      ageFrom,
      ageTo,
      gender,
      source,
      sortOption,
      selectedOrgId,
    ],
    async () => {
      const { sortBy, sortOrder } = jobsSortOptionToParams(sortOption);
      const response = await getJobList({
        page,
        size: pageSize,
        city: cityId,
        district: districtId,
        experience,
        keyword: searchValue,
        state: stateId,
        status: selectedStatus,
        jobType,
        category: categoryId,
        subCategory: subCategoryId,
        startDate,
        endDate,
        ageFrom,
        ageTo,
        gender,
        source,
        sortBy,
        sortOrder,
        orgId: selectedOrgId,
      });
      return response?.data?.data?.data as { total: number; list: JobListingModel[] } | undefined;
    }
  );

  function downloadJobListingExcel() {
    if ((jobList?.total || 0) <= 1000) {
      setExportSubmitting(true);
      downloadJobListingService({
        page: 1,
        size: 1000,
        city: cityId,
        district: districtId,
        keyword: searchValue,
        state: stateId,
        status: selectedStatus,
        jobType,
        experience,
        format: "excel",
        category: categoryId,
        subCategory: subCategoryId,
        startDate,
        endDate,
        ageFrom,
        ageTo,
        gender,
        source,
        orgId: selectedOrgId,
      })
        .then((response) => {
          downloadFromBlob(response, new Date().valueOf() + ".xlsx");
        })
        .catch(() => {
          showSnackbar("Something went wrong while exporting jobs.");
        })
        .finally(() => setExportSubmitting(false));
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
  }

  function updateServiceApiCall({
    statusId,
    jobId,
    comments,
  }: {
    statusId: number;
    jobId: number;
    comments: string;
  }) {
    setActionLoading({ jobId: +jobId, action: statusId === 3 ? "approve" : "reject" });
    updateJobStatus({ comment: comments, statusId, jobId: +jobId! })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Job status updated successfully");
          queryClient.invalidateQueries({ queryKey: ["JobList"] });
        }
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar("Something went wrong");
        }
      })
      .finally(() => setActionLoading(null));
  }

  function deleteJobApiCall({ comments }: { comments: string }) {
    const jobId = +selectedJob?.id!;
    setActionLoading({ jobId, action: "delete" });
    deleteJob({ jobId, comment: comments })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Job removed successfully");
          queryClient.invalidateQueries({ queryKey: ["JobList"] });
        }
      })
      .catch(() => {
        showSnackbar("Something went wrong");
      })
      .finally(() => setActionLoading(null));
    setDeleteDialogOpen(false);
  }

  const count = jobList?.total || 0;
  const list = jobList?.list || [];
  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const summaryText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            fullWidth
            placeholder="Search by company name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <OrgSelector lockToOwnOrg />
          <ToolbarButton
            icon={XLSIcon}
            label="Export"
            onClick={downloadJobListingExcel}
            loading={exportSubmitting}
            disabled={exportSubmitting}
          />
          <ToolbarButton
            icon={SortIcon}
            label={jobsSortOptionLabel(sortOption)}
            onClick={() => setSortDialogOpen(true)}
          />
          <ToolbarButton
            icon={FilterIcon}
            label="Filters"
            badgeCount={activeFilterCount}
            onClick={() => setFilterDialogOpen(true)}
          />
          {hasPermission("jobs", "create") && (
            <Button
              variant="outlined"
              startIcon={<UploadFileRounded />}
              onClick={() => setBulkUploadDialogOpen(true)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Bulk Upload
            </Button>
          )}
          {hasPermission("jobs", "create") && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<AddRounded />}
              onClick={() => navigate("/jobs/add")}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Add Job
            </Button>
          )}
        </Box>
      </Box>

      <BulkUploadJobsDialog open={bulkUploadDialogOpen} onClose={() => setBulkUploadDialogOpen(false)} />

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <Loading />
        </Box>
      ) : isError ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={8} gap={0.5}>
          <Typography fontWeight={600} color={black}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color={greyEnabled}>
            We couldn't load the jobs list. Please try again.
          </Typography>
        </Box>
      ) : showEmpty ? (
        <TableEmptyState message="No jobs found" hint="Try adjusting your search or filters." />
      ) : (
        <DataGridJobListing
          data={list}
          page={page}
          pageSize={pageSize}
          setSelectedJob={setSelectedJob}
          updateServiceApiCall={updateServiceApiCall}
          setRejectDialogOpen={setRejectDialogOpen}
          setDeleteDialogOpen={setDeleteDialogOpen}
          actionLoading={actionLoading}
        />
      )}

      {showList && (
        <CardActions sx={{ px: { xs: 0, sm: 1 }, pb: 0, pt: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              summaryText={summaryText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </CardActions>
      )}

      <FilterJobListing openModal={filterDialogOpen} setOpenModal={setFilterDialogOpen} />

      <SortJobsPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />

      {rejectDialogOpen && (
        <UpdateJobsStatusToReject
          open={rejectDialogOpen}
          onDialogClose={() => setRejectDialogOpen(false)}
          updateServiceStatus={(comments) => {
            updateServiceApiCall({ statusId: 4, comments, jobId: selectedJob?.id! });
            setRejectDialogOpen(false);
          }}
          title="Reject Job listing"
          subtitle="Reason for rejection:"
        />
      )}
      {deleteDialogOpen && (
        <UpdateJobsStatusToDelete
          open={deleteDialogOpen}
          onDialogClose={() => setDeleteDialogOpen(false)}
          updateServiceStatus={(comments) => {
            deleteJobApiCall({ comments });
          }}
          title="Delete Job listing"
          subtitle="Reason for Deletion:"
        />
      )}
    </CardContent>
  );
}
